import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import '../App.css';

function MyGames({ onGameSelect, onNavigateToReview, onStartNewGame, onFindImageGameSelect }) {
  const { user, userProfile } = useAuth();
  const [myGames, setMyGames] = useState([]);
  const [findImageGames, setFindImageGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(true);

  useEffect(() => {
    if (user) {
      loadMyGames();
    }
  }, [user]);

  const loadMyGames = async () => {
    if (!user) return;
    
    try {
      setLoadingGames(true);
      
      if (userProfile?.role === 'host') {
        // For hosts: Load games they created
        const [mainResult, fiResult] = await Promise.all([
          supabase
            .from('games')
            .select('id, game_name, game_date, created_at, updated_at, created_by, game_status, designers')
            .eq('created_by', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('find_image_games')
            .select('id, game_name, created_at, updated_at, created_by, game_status, setup_step, join_code')
            .eq('created_by', user.id)
            .order('created_at', { ascending: false })
        ]);
        
        if (mainResult.error) {
          console.error('Error loading my games:', mainResult.error);
          setMyGames([]);
        } else {
          setMyGames(mainResult.data || []);
        }

        if (fiResult.error) {
          console.error('Error loading find image games:', fiResult.error);
          setFindImageGames([]);
        } else {
          setFindImageGames(fiResult.data || []);
        }
      } else {
        // For designers/players: Load games they've joined via game_participants
        const [mainResult, fiResult] = await Promise.all([
          supabase
            .from('game_participants')
            .select(`
              game_id,
              games!inner(id, game_name, game_date, created_at, updated_at, created_by, game_status, designers)
            `)
            .eq('user_id', user.id)
            .order('joined_at', { ascending: false }),
          supabase
            .from('find_image_participants')
            .select(`
              game_id,
              find_image_games!inner(id, game_name, created_at, updated_at, created_by, game_status, join_code)
            `)
            .eq('user_id', user.id)
            .order('joined_at', { ascending: false })
        ]);
        
        if (mainResult.error) {
          console.error('Error loading joined games:', mainResult.error);
          setMyGames([]);
        } else if (mainResult.data) {
          const games = mainResult.data.map(p => p.games).filter(Boolean);
          setMyGames(games);
        } else {
          setMyGames([]);
        }

        if (fiResult.error) {
          console.error('Error loading find image games:', fiResult.error);
          setFindImageGames([]);
        } else if (fiResult.data) {
          const games = fiResult.data.map(p => p.find_image_games).filter(Boolean);
          setFindImageGames(games);
        } else {
          setFindImageGames([]);
        }
      }
    } catch (error) {
      console.error('Error loading my games:', error);
      setMyGames([]);
      setFindImageGames([]);
    } finally {
      setLoadingGames(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const handleDeleteGame = async (game, e) => {
    e.stopPropagation(); // Prevent triggering the row click
    
    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete "${game.game_name || 'Untitled Game'}"?\n\n` +
      `This will permanently delete:\n` +
      `• All participants\n` +
      `• All client changes\n` +
      `• All session briefs\n` +
      `• All voice clips\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      // Delete the game (cascade will handle related records)
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', game.id)
        .eq('created_by', user.id); // Extra safety: only delete if user created it
      
      if (error) {
        throw error;
      }
      
      // Reload games list
      await loadMyGames();
      
      // Show success message
      alert('Game deleted successfully!');
    } catch (error) {
      console.error('Error deleting game:', error);
      alert(`Failed to delete game: ${error.message || 'Please try again.'}`);
    }
  };

  const handleDeleteFindImageGame = async (game, e) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to delete "${game.game_name || 'Untitled Game'}"?\n\nThis will permanently delete the game and all participant data.\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const { error } = await supabase
        .from('find_image_games')
        .delete()
        .eq('id', game.id)
        .eq('created_by', user.id);
      if (error) throw error;
      await loadMyGames();
      alert('Game deleted successfully!');
    } catch (error) {
      console.error('Error deleting find image game:', error);
      alert(`Failed to delete game: ${error.message || 'Please try again.'}`);
    }
  };

  const totalGames = myGames.length + findImageGames.length;

  return (
    <div className="section" style={{ maxWidth: '1000px', width: '90%', margin: '0 auto' }}>
      {loadingGames ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          Loading your games...
        </div>
      ) : totalGames === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            {userProfile?.role === 'host' 
              ? "You haven't created any games yet." 
              : "You haven't joined any games yet."}
          </p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: '1.5rem', color: 'rgba(255, 255, 255, 0.5)' }}>
            {userProfile?.role === 'host' 
              ? 'Create your first game to get started!' 
              : 'Join a game using a join code to get started!'}
          </p>
          {onStartNewGame && userProfile?.role === 'host' && (
            <button
              onClick={onStartNewGame}
              style={{
                padding: '0.75rem 2rem',
                background: '#D702F6',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#C001E5';
                e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#D702F6';
                e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Start New Game
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Creative Coaster Games */}
          {myGames.length > 0 && (
            <div>
              <h3 style={{ 
                margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: 600, 
                textTransform: 'uppercase', letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.5)'
              }}>
                Creative Coaster
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {myGames.map((game) => {
                  const status = game.game_status || 'draft';
                  const statusConfig = {
                    draft: { text: 'Draft', bg: 'rgba(255, 193, 7, 0.2)', color: '#FFC107', border: 'rgba(255, 193, 7, 0.3)' },
                    published: { text: 'Published', bg: 'rgba(33, 150, 243, 0.2)', color: '#2196F3', border: 'rgba(33, 150, 243, 0.3)' },
                    started: { text: 'In Progress', bg: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50', border: 'rgba(76, 175, 80, 0.3)' },
                    finished: { text: 'Finished', bg: 'rgba(158, 158, 158, 0.2)', color: '#9E9E9E', border: 'rgba(158, 158, 158, 0.3)' }
                  };
                  const config = statusConfig[status] || statusConfig.draft;

                  return (
                    <div
                      key={game.id}
                      onClick={() => onGameSelect && onGameSelect(game)}
                      style={{
                        padding: '1.5rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#D702F6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(215, 2, 246, 0.3)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>
                              {game.game_name || 'Untitled Game'}
                            </h3>
                            <span style={{
                              display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '12px',
                              background: config.bg, color: config.color, fontSize: '0.75rem',
                              fontWeight: 600, border: `1px solid ${config.border}`
                            }}>
                              {config.text}
                            </span>
                          </div>
                          <p style={{ margin: '0.5rem 0 0 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem', fontWeight: 500 }}>
                            {formatDate(game.game_date || game.created_at)}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {onNavigateToReview && (game.game_status === 'published' || game.game_status === 'started' || game.game_status === 'finished') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onNavigateToReview(game); }}
                              style={{ 
                                backgroundColor: '#D702F6', border: '2px solid #D702F6', color: '#ffffff',
                                padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer',
                                fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center',
                                gap: '0.5rem', transition: 'background-color 0.3s ease, border-color 0.3s ease'
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = '#C001E5'; e.target.style.borderColor = '#C001E5'; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = '#D702F6'; e.target.style.borderColor = '#D702F6'; }}
                            >
                              {game.game_status === 'finished' ? 'View Scores' : 'Review & Score'}
                            </button>
                          )}
                          {userProfile?.role === 'host' && game.created_by === user.id && (
                            <button
                              onClick={(e) => handleDeleteGame(game, e)}
                              style={{ 
                                backgroundColor: 'transparent', border: '2px solid rgba(255, 87, 34, 0.5)',
                                color: '#FF5722', padding: '0.5rem 1rem', borderRadius: '6px',
                                cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                                display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255, 87, 34, 0.1)'; e.target.style.borderColor = '#FF5722'; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(255, 87, 34, 0.5)'; }}
                              title="Delete this game"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Find The Image Games */}
          {findImageGames.length > 0 && (
            <div>
              <h3 style={{ 
                margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: 600, 
                textTransform: 'uppercase', letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.5)'
              }}>
                Find The Image
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {findImageGames.map((game) => {
                  const status = game.game_status || 'setup';
                  const fiStatusConfig = {
                    setup: { text: `Draft (Step ${game.setup_step || 1}/4)`, bg: 'rgba(255, 193, 7, 0.2)', color: '#FFC107', border: 'rgba(255, 193, 7, 0.3)' },
                    ready: { text: 'Published', bg: 'rgba(33, 150, 243, 0.2)', color: '#2196F3', border: 'rgba(33, 150, 243, 0.3)' },
                    active: { text: 'In Progress', bg: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50', border: 'rgba(76, 175, 80, 0.3)' },
                    finished: { text: 'Finished', bg: 'rgba(158, 158, 158, 0.2)', color: '#9E9E9E', border: 'rgba(158, 158, 158, 0.3)' }
                  };
                  const config = fiStatusConfig[status] || fiStatusConfig.setup;

                  return (
                    <div
                      key={game.id}
                      onClick={() => onFindImageGameSelect && onFindImageGameSelect(game)}
                      style={{
                        padding: '1.5rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#D702F6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(215, 2, 246, 0.3)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, color: '#ffffff', fontSize: '1.1rem', fontWeight: 600 }}>
                              {game.game_name || 'Untitled Game'}
                            </h3>
                            <span style={{
                              display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '12px',
                              background: config.bg, color: config.color, fontSize: '0.75rem',
                              fontWeight: 600, border: `1px solid ${config.border}`
                            }}>
                              {config.text}
                            </span>
                          </div>
                          <p style={{ margin: '0.5rem 0 0 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem', fontWeight: 500 }}>
                            {formatDate(game.created_at)}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {status === 'setup' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onFindImageGameSelect && onFindImageGameSelect(game); }}
                              style={{ 
                                backgroundColor: '#D702F6', border: '2px solid #D702F6', color: '#ffffff',
                                padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer',
                                fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center',
                                gap: '0.5rem', transition: 'background-color 0.3s ease'
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = '#C001E5'; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = '#D702F6'; }}
                            >
                              Continue Setup
                            </button>
                          )}
                          {userProfile?.role === 'host' && game.created_by === user.id && (
                            <button
                              onClick={(e) => handleDeleteFindImageGame(game, e)}
                              style={{ 
                                backgroundColor: 'transparent', border: '2px solid rgba(255, 87, 34, 0.5)',
                                color: '#FF5722', padding: '0.5rem 1rem', borderRadius: '6px',
                                cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                                display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255, 87, 34, 0.1)'; e.target.style.borderColor = '#FF5722'; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(255, 87, 34, 0.5)'; }}
                              title="Delete this game"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MyGames;

